import httpClient from "@plugins/http";
import appConfigs from "@configs/app";
import { ApiResponse } from "@apis/api.ts";
import { SectionInfoModel } from "@modules/calendar/task/types.ts";
import {
  Assignment,
  RecentAssignmentSortParams,
  BulkAssignAssignmentsRequestModel,
  LinkedAssignmentsCompletion,
} from "@modules/assignment/assignment-types.ts";
import { CategoryNotificationMessage } from "@/modules/attempt/types";
import { AssignmentMetadata } from "@/types/assignment";
import { AssignmentStudentModel } from "@/modules/study-buddy/types";

const fetchAllMyStudentGroups = () => {
  return httpClient.get(`/apis/assignment/studentgroups/all`);
};

const fetchMyStudentGroups = (studentGroupIds) => {
  return httpClient.get(`/apis/assignment/studentgroups?studentGroupIds=${studentGroupIds}`);
};

const fetchStudentGroup = async (uuid): Promise<ApiResponse<object>> => {
  return await httpClient.get(`/apis/assignment/studentgroup/${uuid}`);
};

const fetchAssignmentMetadata = async (
  assignmentUuid,
): Promise<ApiResponse<AssignmentMetadata>> => {
  return await httpClient.get(`/apis/assignment/metadata/${assignmentUuid}`);
};

const createAssignment = (params) => {
  return httpClient.post(`/apis/assignment/create`, params);
};

const checkAssignmentCreationStatus = async (params): Promise<ApiResponse<boolean>> => {
  return await httpClient.get(`/apis/assignment/creation-status?assignmentUuids=${params}`);
};

const saveAssignment = (params) => {
  return httpClient.post(`/apis/assignment/save`, params);
};

const checkstudentmorights = (params) => {
  return httpClient.get(`/apis/assignment/checkstudentmorights`, {
    params: {
      resourceUuid: params.resourceUuid,
    },
  });
};
const deleteAssignment = (assignmentUuid, browserTabUuid) => {
  return httpClient.post(`/apis/assignment/delete`, {
    uuid: assignmentUuid,
    browserTabUuid,
  });
};

const fetchDashboardAssignments = async (
  params: RecentAssignmentSortParams,
): Promise<ApiResponse<Assignment[]>> => {
  return await httpClient.get(`/apis/assignment/list-recent`, { params });
};

const fetchAssignments = (params) => {
  return httpClient.post(`/apis/assignment/search`, params);
};

const fetchAssessments = (params) => {
  return httpClient.post(`/apis/assignment/search-assessment`, params);
};

const savePageAccessSetting = (requestData) => {
  const params = new URLSearchParams();
  params.append("assignmentUuid", requestData.assignmentUuid);
  params.append("activityIds", requestData.activityIds);
  params.append("isAccessible", requestData.isAccessible);
  return httpClient.post("/apis/assignment/activity/settings/save", params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

const fetchAssignmentStudents = (uuid): Promise<ApiResponse<AssignmentStudentModel[]>> => {
  return httpClient.get(`/apis/assignment/get-students/${uuid}`);
};

const fetchClassListingAssignmentStudents = (uuid) => {
  return httpClient.get(`/apis/assignment/students/assigned/${uuid}`);
};

const fetchAssignmentActivityStudents = (assignmentUuid, activityId) => {
  const requestParam = activityId ? `?activityId=${activityId}` : ``;
  return httpClient.get(
    `/apis/assignment/activity/students/access/${assignmentUuid}${requestParam}`,
  );
};

const fetchAssignmentTeachers = (uuid) => {
  return httpClient.get(`/apis/assignment/get-teachers/${uuid}`);
};

const copyAssignmentLesson = (params) => {
  return httpClient.post(`/apis/assignment/copy-to-drive`, params);
};

const pauseAssignment = (assignmentUuid) => {
  return httpClient.post(`/apis/assignment/pause/${assignmentUuid}`);
};

const resumeAssignment = (requestData) => {
  const params = new URLSearchParams();

  params.append("notifyStudents", requestData.notifyStudents);
  params.append("notificationMsg", requestData.notificationMsg || "");
  params.append("markAsIncomplete", requestData.markAsIncomplete);

  if (requestData.browserTabUuid !== null && requestData.browserTabUuid !== undefined) {
    params.append("browserTabUuid", requestData.browserTabUuid);
  }

  return httpClient.post(`/apis/assignment/resume/${requestData.assignmentUuid}`, params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

const fetchAssignmentProgress = (
  { assignmentUuid, year }: { assignmentUuid: string; year: string },
  signedParams: URLSearchParams,
): Promise<ApiResponse<object>> => {
  const params = new URLSearchParams(signedParams);
  params.append("t", Date.now().toString());
  return httpClient.get(
    `${appConfigs.s3ResourceDomain}/assignment/${year}/${assignmentUuid}/${appConfigs.s3AssignmentDomainJSONVersion}/progress.json`,
    {
      withCredentials: true,
      params,
    },
  );
};

const fetchCourseSectionInfo = async (
  uuids: string[],
): Promise<ApiResponse<Record<string, SectionInfoModel>>> => {
  return await httpClient.post(`/apis/assignment/course/section-info`, uuids);
};

const moveAssignment = (params) => {
  return httpClient.post("/apis/assignment/move", params);
};

const moveAssignmentFromPastYearToCurrentInStudentGroup = (params) => {
  return httpClient.post("/apis/assignment/move/past", params);
};

const getAssignmentNotifications = async (
  uuid,
): Promise<ApiResponse<CategoryNotificationMessage[]>> => {
  return await httpClient.get(`/apis/assignment/notifications/${uuid}`);
};

const checkAssignmentExaminable = async (assignmentUuid): Promise<ApiResponse<boolean>> => {
  return await httpClient.get(`/apis/assignment/check-assignment-examinable/${assignmentUuid}`);
};

const checkActivitiesAttempted = (assignmentUuid) => {
  return httpClient.get(`/apis/assignment/check-activities-attempted/${assignmentUuid}`);
};

//Todo Johnny: put all configuration API to same js
const fetchSystemAcademicYear = () => {
  return httpClient.get("/apis/configuration/systemAcademicYear");
};

const pauseLinkedAssignments = (assignmentUuid) => {
  return httpClient.post(`/apis/linked-assignments/pause/${assignmentUuid}`);
};

const resumeLinkedAssignments = async (requestData) => {
  const params = new URLSearchParams();

  params.append("notifyStudents", requestData.notifyStudents);
  params.append("notificationMsg", requestData.notificationMsg || "");
  params.append("markAsIncomplete", requestData.markAsIncomplete);

  if (requestData.browserTabUuid !== null && requestData.browserTabUuid !== undefined) {
    params.append("browserTabUuid", requestData.browserTabUuid);
  }

  return await httpClient.post(
    `/apis/linked-assignments/resume/${requestData.assignmentUuid}`,
    params,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
};

const fetchLinkedAssignmentsList = async (moduleUuid) => {
  return httpClient.post(`/apis/linked-assignments/list/${moduleUuid}`);
};

const fetchLinkedAssignmentsCount = (assignmentUuid) => {
  return httpClient.get(`/apis/linked-assignments/count/${assignmentUuid}`);
};

const getLinkedAssignmentsModuleCompletion = async (
  moduleUuid: string,
): Promise<ApiResponse<Record<string, LinkedAssignmentsCompletion>>> => {
  return await httpClient.get(`/apis/linked-assignments/module/${moduleUuid}/completion`);
};

const getLinkedAssignmentsSectionActivityCompletion = async (
  moduleUuid: string,
  sectionActivityId: number,
): Promise<ApiResponse<LinkedAssignmentsCompletion[]>> => {
  return await httpClient.get(
    `/apis/linked-assignments/module/${moduleUuid}/activities/${sectionActivityId}/completion`,
  );
};

const getAssignmentProgressSignedUrl = async ({
  assignmentUuid,
}: {
  assignmentUuid: string;
}): Promise<ApiResponse<string>> => {
  return httpClient.get(`/apis/assignment/progress/json/generate-signed-url/${assignmentUuid}`);
};

const bulkAssignAssignments = async (
  requestData: BulkAssignAssignmentsRequestModel,
): Promise<ApiResponse<boolean>> => {
  return await httpClient.post(`/apis/assignment/bulk-assign`, requestData);
};

const fetchDeletedAssignments = async ({
  studentGroupCollectionId,
  studentGroupResourceType,
  eAssessment,
}: {
  studentGroupCollectionId?: number;
  studentGroupResourceType: string;
  eAssessment?: boolean;
}): Promise<ApiResponse<object[]>> => {
  const params: Record<string, unknown> = { studentGroupResourceType };
  if (studentGroupCollectionId !== undefined) {
    params.studentGroupCollectionId = studentGroupCollectionId;
  }
  if (eAssessment !== undefined) {
    params.eAssessment = eAssessment;
  }
  return await httpClient.get(`/apis/assignment/deleted-list`, { params });
};

const restoreAssignment = async (assignmentUuid: string): Promise<ApiResponse<object>> => {
  return await httpClient.post(`/apis/assignment/restore`, { uuid: assignmentUuid });
};

export default {
  fetchAllMyStudentGroups,
  fetchMyStudentGroups,
  fetchStudentGroup,
  fetchAssignmentMetadata,
  createAssignment,
  checkAssignmentCreationStatus,
  saveAssignment,
  checkstudentmorights,
  deleteAssignment,
  fetchDashboardAssignments,
  fetchAssignments,
  fetchAssessments,
  savePageAccessSetting,
  fetchAssignmentStudents,
  fetchClassListingAssignmentStudents,
  fetchAssignmentActivityStudents,
  fetchAssignmentTeachers,
  copyAssignmentLesson,
  pauseAssignment,
  resumeAssignment,
  fetchAssignmentProgress,
  fetchCourseSectionInfo,
  moveAssignment,
  moveAssignmentFromPastYearToCurrentInStudentGroup,
  getAssignmentNotifications,
  checkAssignmentExaminable,
  checkActivitiesAttempted,
  fetchSystemAcademicYear,
  pauseLinkedAssignments,
  resumeLinkedAssignments,
  fetchLinkedAssignmentsList,
  fetchLinkedAssignmentsCount,
  getLinkedAssignmentsModuleCompletion,
  getLinkedAssignmentsSectionActivityCompletion,
  getAssignmentProgressSignedUrl,
  bulkAssignAssignments,
  fetchDeletedAssignments,
  restoreAssignment,
};
